import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import DoctorFormModal from "./DoctorFormModal";
import { doctorMatchesSearch } from "../../utils/doctorHelpers";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoctorSelect({
  token,
  value,
  displayName,
  onSelect,
  onClear,
  disabled = false,
  className,
}) {
  const { activeStruttura } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  const fetchDoctors = async (q) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = q?.trim() ? { q: q.trim() } : {};
      const res = await axios.get(`${API_URL}/api/doctors`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDoctors(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchDoctors(search);
  }, [open, token, activeStruttura?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchDoctors(search), 200);
    return () => clearTimeout(t);
  }, [search, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => doctors.filter((d) => doctorMatchesSearch(d, search)),
    [doctors, search]
  );

  const selectedDoctor = doctors.find((d) => d.id === value);
  const label =
    selectedDoctor?.name ||
    (displayName?.trim() ? displayName : null) ||
    "Seleziona medico...";

  const handleDoctorCreated = (doctor) => {
    setDoctors((prev) => {
      const exists = prev.some((d) => d.id === doctor.id);
      if (exists) return prev;
      return [...prev, doctor].sort((a, b) => a.name.localeCompare(b.name, "it"));
    });
    onSelect?.(doctor);
    setOpen(false);
  };

  return (
    <>
      <div className={cn("flex items-center gap-1.5", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "mt-1 flex-1 justify-between font-normal h-10",
                !value && !displayName?.trim() && "text-muted-foreground"
              )}
            >
              <span className="truncate">{label}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Cerca per nome o cognome..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <>
                    <CommandEmpty>Nessun medico trovato</CommandEmpty>
                    <CommandGroup>
                      {value && (
                        <CommandItem
                          value="__clear__"
                          onSelect={() => {
                            onClear?.();
                            setOpen(false);
                          }}
                          className="text-slate-500"
                        >
                          Rimuovi selezione
                        </CommandItem>
                      )}
                      {filtered.map((doctor) => (
                        <CommandItem
                          key={doctor.id}
                          value={doctor.id}
                          onSelect={() => {
                            onSelect?.(doctor);
                            setOpen(false);
                            setSearch("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === doctor.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="min-w-0">
                            <p className="truncate">{doctor.name}</p>
                            {doctor.specialization ? (
                              <p className="text-xs text-slate-500 truncate">
                                {doctor.specialization}
                              </p>
                            ) : null}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          size="icon"
          disabled={disabled}
          onClick={() => setShowNewModal(true)}
          className="mt-1 h-10 w-10 shrink-0 bg-violet-600 hover:bg-violet-700"
          title="Aggiungi nuovo medico"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <DoctorFormModal
        open={showNewModal}
        onOpenChange={setShowNewModal}
        token={token}
        onSaved={handleDoctorCreated}
      />
    </>
  );
}
